/*
 * Correlations.tsx — S57: Correlation Groups Panel
 * API: GET /api/v1/correlations/groups
 */
import { useState, useEffect } from 'react';
import { API_V1, safeFetch } from '../constants';

interface CorrGroup {
  id: number;
  correlation_type: string;
  correlation_confidence: number | null;
  group_status: string;
  exception_ids: number[];
  metadata: Record<string, any>;
  created_at: string | null;
}

export default function Correlations() {
  const [groups, setGroups] = useState<CorrGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    safeFetch(`${API_V1}/correlations/groups`, { groups: [] })
      .then(d => { setGroups(d.groups || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading correlation groups...</p></div>;
  if (error) return (
    <div className="main" style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  const typeColor = (t: string) => {
    if (t === 'IC_MISMATCH') return 'var(--blue)';
    if (t === 'SPLIT_TRANSACTION') return 'var(--amber)';
    return 'var(--text-secondary)';
  };

  const statusBadge = (s: string) => {
    const colors: Record<string, string> = {
      DETECTED: 'var(--amber)', REVIEWING: 'var(--blue)',
      RESOLVED: 'var(--green)', DISMISSED: 'var(--text-tertiary)',
    };
    return (
      <span style={{
        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
        background: `${colors[s] || 'var(--text-tertiary)'}20`,
        color: colors[s] || 'var(--text-tertiary)',
      }}>
        {s}
      </span>
    );
  };

  const icCount = groups.filter(g => g.correlation_type === 'IC_MISMATCH').length;
  const splitCount = groups.filter(g => g.correlation_type === 'SPLIT_TRANSACTION').length;

  return (
    <div className="main" style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'IC Matches', value: icCount, color: 'var(--blue)' },
          { label: 'Split Transactions', value: splitCount, color: 'var(--amber)' },
          { label: 'Total Groups', value: groups.length, color: 'var(--text-primary)' },
        ].map(k => (
          <div key={k.label} style={{ padding: '16px 20px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {groups.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-tertiary)' }}>No correlation groups detected yet.</p>
        </div>
      ) : (
        groups.map(g => (
          <div key={g.id} className="card" style={{ padding: 20, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: typeColor(g.correlation_type) }}>{g.correlation_type.replace(/_/g, ' ')}</span>
                {statusBadge(g.group_status)}
              </div>
              <span className="mono" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Group #{g.id}</span>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
              <span>Confidence: <span className="mono" style={{ color: (g.correlation_confidence ?? 0) >= 0.9 ? 'var(--green)' : 'var(--amber)' }}>{g.correlation_confidence != null ? `${(g.correlation_confidence * 100).toFixed(0)}%` : 'N/A'}</span></span>
              <span>Exceptions: {g.exception_ids?.length || 0}</span>
              {g.created_at && <span>Detected: {new Date(g.created_at).toLocaleDateString()}</span>}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
